import type {ReactElement} from 'react';
import React from 'react';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromLineItem} from '@Hooks/poster-editor/useGetKeyFromLineItem';
import {ContextualMenuDropdownOption} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {LineDashType} from '@PosterWhiteboard/items/line-item/line-item.types';
import styles from './line-dash-option.module.scss';
import {CustomListItem} from '@Components/contextual-menu/components/custom-list-item';
import {getSelectorSvgsFromType, getSvgFromType} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-dash-option/line-dash-svgs';
import {CustomDashSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-dash-option/components/custom-dash-size-option/custom-dash-size-option';
import {CustomGapSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-dash-option/components/custom-gap-size-option/custom-gap-size-option';
import {
  getLineDashOptions,
  onUpdateDashSize,
  onUpdateGapSize,
} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-dash-option/line-dash-option.library';
import {
  DEFAULT_CUSTOM_DASH_SIZE,
  DEFAULT_CUSTOM_GAP_SIZE,
} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-dash-option/line-dash-options.types';

export function LineDashOption(): ReactElement {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemLineDashType = useGetKeyFromLineItem('lineDashType');
  const activeItemCustomDashSize = useGetKeyFromLineItem('customDashSize');
  const activeItemCustomGapSize = useGetKeyFromLineItem('customGapSize');

  if (!activeItem || activeItemLineDashType === undefined || activeItemCustomDashSize === undefined || activeItemCustomGapSize === undefined) {
    throw new Error('activeItem does not exist in ItemShadowOption');
  }

  const onClick = (type: LineDashType, undoable = true): void => {
    if (activeItemLineDashType === type) {
      return;
    }
    const updateObject: {lineDashType: LineDashType; customDashSize?: number; customGapSize?: number} = {
      lineDashType: type,
    };

    if (type === LineDashType.CUSTOM) {
      updateObject.customDashSize = DEFAULT_CUSTOM_DASH_SIZE;
      updateObject.customGapSize = DEFAULT_CUSTOM_GAP_SIZE;
    }

    console.log(updateObject);
    void activeItem.updateFromObject(updateObject, {
      undoable,
    });
  };

  const getLineDashOptionsList = (): ReactElement[] => {
    const options = getLineDashOptions();
    return options.map((option) => {
      return (
        <CustomListItem
          id={option.id}
          key={option.id}
          svgOrIcon={option.id !== LineDashType.CUSTOM ? getSvgFromType(option.id, activeItemLineDashType === option.id) : undefined}
          isSelected={activeItemLineDashType === option.id}
          onClick={(): void => {
            onClick(option.id);
          }}
          text={option.id === LineDashType.CUSTOM ? window.i18next.t('pmwjs_custom') : undefined}
          svgClassName={styles.svgContainer}
        />
      );
    });
  };

  const onUpdateCustomDashSize = (dashSize: number, undoable = true): void => {
    onUpdateDashSize(activeItem, dashSize, undoable);
  };
  const getCustomLineDashSubOption = (): ReactElement => {
    return <CustomDashSizeOption dashSize={activeItemCustomDashSize} onUpdateDashSize={onUpdateCustomDashSize} />;
  };

  const onUpdateCustomGapSize = (gapSize: number, undoable = true): void => {
    onUpdateGapSize(activeItem, gapSize, undoable);
  };

  const getCustomGapSizeSubOption = (): ReactElement => {
    return <CustomGapSizeOption gapSize={activeItemCustomGapSize} onUpdateGapSize={onUpdateCustomGapSize} />;
  };

  const getSubOption = (): ReactElement | null => {
    if (activeItemLineDashType === LineDashType.CUSTOM) {
      return (
        <>
          {getCustomLineDashSubOption()}
          {getCustomGapSizeSubOption()}
        </>
      );
    }
    return null;
  };

  const getCustomPopup = (): ReactElement => {
    return (
      <div className={styles.popupContainer}>
        <ul className={`spacing-m-0 ${styles.popup}`}>{getLineDashOptionsList()}</ul>
      </div>
    );
  };

  return (
    <ContextualMenuDropdownOption
      id={'line-dash-option'}
      label={window.i18next.t('pmwjs_line_type')}
      customPopup={getCustomPopup()}
      selectorSvg={getSelectorSvgsFromType(activeItemLineDashType)}
      selectorSvgClassName={styles.selectorSvg}
      dropdownSelectorText={activeItemLineDashType === LineDashType.CUSTOM ? window.i18next.t('pmwjs_custom') : undefined}
    >
      {getSubOption()}
    </ContextualMenuDropdownOption>
  );
}
